import { Stack } from '@mui/material';
import { useState } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { TypeaheadSelectInput } from '@/components/form/baseInputs/TypeaheadSelectInput/TypeaheadSelectInput';
import {
  DialogModal,
  DialogModalProps,
} from '@/components/modals/DialogModal/DialogModal';
import { Callout } from '@/components/notifications/Callout/Callout';
import { EntityType } from '@/modules/entities/types/EntityType';

import { CopyBeneficiaryDesignationsResult } from './CopyBeneficiaryDesignationsModal.types';
import {
  getCopyBeneficiaryDesignationsResult,
  useCopyBeneficiaryDesignationOptions,
} from './CopyBeneficiaryDesignationsModal.utils';

interface CopyBeneficiaryDesignationsModalProps
  extends Omit<DialogModalProps, 'onClose' | 'heading'> {
  onClose: (result: CopyBeneficiaryDesignationsResult | null) => void;
  hasRecipients: boolean;
  entityType: EntityType;
  tier: 'primary' | 'contingent';
  currentEntityId: string | null;
}

export function CopyBeneficiaryDesignationsModal({
  onClose,
  hasRecipients,
  entityType,
  tier,
  currentEntityId,
  ...modalProps
}: CopyBeneficiaryDesignationsModalProps) {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selectedSourceEntityId, setSelectedSourceEntityId] = useState<
    string | null
  >(null);

  const { options, data, targetTypename, targetTier } =
    useCopyBeneficiaryDesignationOptions({
      entityType,
      tier,
      currentEntityId,
    });

  function handleCopy() {
    if (!data) return;
    if (!selectedSourceEntityId) {
      setErrorMessage('Please select a source to copy beneficiaries from');
      return;
    }

    const result = getCopyBeneficiaryDesignationsResult(
      selectedSourceEntityId,
      targetTypename,
      targetTier,
      data
    );
    if (!result) {
      setErrorMessage('No beneficiaries to copy');
      return;
    }

    onClose(result);
  }

  function handleChange(value: string | null) {
    setErrorMessage(null);
    setSelectedSourceEntityId(value);
  }

  return (
    <DialogModal
      heading="Select a source to copy beneficiaries from"
      onClose={() => onClose(null)}
      {...modalProps}
    >
      <Stack spacing={2}>
        <TypeaheadSelectInput<string>
          placeholder="Select a source"
          value={selectedSourceEntityId ?? ''}
          onChange={(_e, value) => handleChange(value)}
          options={options}
          label="Source to copy from"
          hideLabel
          errorMessage={errorMessage ?? undefined}
          data-testid="copy-beneficiaries-from-select"
        />

        {hasRecipients && (
          <Callout severity="warning">
            Any existing beneficiaries will be replaced with those from the
            source selected above
          </Callout>
        )}

        <Button variant="primary" size="sm" fullWidth onClick={handleCopy}>
          Copy beneficiaries
        </Button>
      </Stack>
    </DialogModal>
  );
}
